import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

/**
 * Tariffs (CR-4) — the revenue side of a UJP, in its own `tariff` module under
 * `/v1/tariffs`. A client's tariff config (dated) + its rings price a trip; the
 * UJP tags a `tariffConfigId` / `ringId` at create and the server snapshots
 * `revenue_amount` / `margin_amount` at approve. This is the counterpart to the
 * COST-side `ujp_client_configs` (CR-1) — two client-scoped configs, different
 * concerns, no merge.
 *
 * Writes are approver-gated (403 otherwise). Money travels as STRINGS.
 */

export type TariffTipe =
  | 'PER_TRIP_FLAT'
  | 'FIXED'
  | 'PER_RING'
  | 'DISTANCE_TIER';
export const TARIFF_TIPES: readonly TariffTipe[] = [
  'PER_TRIP_FLAT',
  'FIXED',
  'PER_RING',
  'DISTANCE_TIER',
];

/** Day-of-week code of `hariOperasi` (CR4b-D7). `[]` on a config = every day. */
export type HariCode = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';
export const HARI_CODES: readonly HariCode[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
];

/** Shift code of `shiftOperasi` (CR4b-D7). `[]` on a config = every shift. */
export type ShiftCode = 'Dawn' | 'Morning' | 'Noon' | 'Night';
export const SHIFT_CODES: readonly ShiftCode[] = [
  'Dawn',
  'Morning',
  'Noon',
  'Night',
];

export type MoneyString = string;
export type NumberLike = string | number;

/**
 * Per-body rate override map (CR4b-D2): key = the UJP's `unitType` string
 * (matched trimmed + case-insensitive server-side), value = money string. A
 * `"0"` / absent entry falls back to the base rate.
 */
export type BodyRateMap = Record<string, MoneyString>;

/** One DISTANCE_TIER step (CR4b-D3): `km ≤ maxKm` (inclusive) → `rate`. */
export interface DistanceTier {
  maxKm: number;
  rate: MoneyString;
}

// ─────────────────────────────────────────────────────────────────────────────
// Shapes
// ─────────────────────────────────────────────────────────────────────────────

/** A ring under a PER_RING config (CR4 §18.1: rings are config-scoped). */
export interface TariffRing {
  id: string;
  tariffConfigId: string;
  nama: string;
  urutan: number;
  ratePerTrip: MoneyString;
  ratePerTripMulti: MoneyString;
  // ── CR-4b ──────────────────────────────────────────────────────────────────
  /** Per-body overrides, single / multi drop (CR4b-D2, D10). */
  ratesPerBody: BodyRateMap;
  ratesPerBodyMulti: BodyRateMap;
  /** Komisi shares (COST side, CR4b-D4); multi falls back to single when 0. */
  driverShare: MoneyString;
  helperShare: MoneyString;
  driverShareMulti: MoneyString;
  helperShareMulti: MoneyString;
  keterangan: string;
  /**
   * An inactive ring stays on the config (approved UJPs snapshot it) but is
   * hidden from the wizard picker / auto-suggest and skipped by the server's
   * single-ring fallback (`RING_AUTO_SINGLE`).
   */
  active: boolean;
}

export interface TariffConfig {
  id: string;
  clientId: number;
  tipe: TariffTipe;
  rateFlat: MoneyString;
  rateFlatMulti: MoneyString;
  rateFixed: MoneyString;
  asuransi: MoneyString;
  /**
   * Legacy (CR4b-D8): nothing is unsupported any more; kept for old rows. The
   * UI only flags it when it is explicitly `false`.
   */
  modeSupported: boolean;
  // ── CR-4b ──────────────────────────────────────────────────────────────────
  /** PER_TRIP_FLAT per-body overrides, single / multi (CR4b-D2, D10). */
  ratesFlatPerBody: BodyRateMap;
  ratesFlatPerBodyMulti: BodyRateMap;
  /** PER_TRIP_FLAT komisi shares (CR4b-D4). */
  driverShareFlat: MoneyString;
  helperShareFlat: MoneyString;
  driverShareFlatMulti: MoneyString;
  helperShareFlatMulti: MoneyString;
  /** Revenue-side reverse charge, added when the UJP is reverse (CR4b-D6). */
  hargaReverse: MoneyString;
  /** Applicability windows (CR4b-D7); empty = every day / every shift. */
  hariOperasi: HariCode[];
  shiftOperasi: ShiftCode[];
  /** Stored + shown only; applied by the Laporan Margin (CR4b-D9). */
  tagihPerRit: boolean;
  /** Used when `tipe = DISTANCE_TIER` (CR4b-D3). */
  distanceTiers: DistanceTier[];
  /** Multidrop surcharge (CR4b-D5): `max(0, titik − free) × extraRate`; "0" rate = off. */
  multidropFreePoints: number;
  multidropExtraRate: MoneyString;
  catatan: string;
  /** `YYYY-MM-DD`. */
  berlakuMulai: string;
  berlakuSampai: string | null;
  active: boolean;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on the detail / when the list eager-loads them. */
  rings?: TariffRing[];
}

// ── Requests ─────────────────────────────────────────────────────────────────

export interface TariffListParams {
  clientId?: number;
  /** `YYYY-MM-DD` — resolve the config effective on that day. */
  activeOn?: string;
}

export interface CreateTariffPayload {
  clientId: number;
  tipe: TariffTipe;
  rateFlat?: MoneyString;
  rateFlatMulti?: MoneyString;
  rateFixed?: MoneyString;
  asuransi?: MoneyString;
  berlakuMulai: string;
  berlakuSampai?: string | null;
  // ── CR-4b (all optional; absent = server default / unchanged on PATCH) ─────
  ratesFlatPerBody?: BodyRateMap;
  ratesFlatPerBodyMulti?: BodyRateMap;
  driverShareFlat?: MoneyString;
  helperShareFlat?: MoneyString;
  driverShareFlatMulti?: MoneyString;
  helperShareFlatMulti?: MoneyString;
  hargaReverse?: MoneyString;
  hariOperasi?: HariCode[];
  shiftOperasi?: ShiftCode[];
  tagihPerRit?: boolean;
  distanceTiers?: DistanceTier[];
  multidropFreePoints?: number;
  multidropExtraRate?: MoneyString;
  catatan?: string;
}

export type UpdateTariffPayload = Partial<
  Omit<CreateTariffPayload, 'clientId'>
> & { active?: boolean };

export interface CreateRingPayload {
  nama: string;
  urutan?: number;
  ratePerTrip: MoneyString;
  ratePerTripMulti?: MoneyString;
  // ── CR-4b (all optional; absent = server default / unchanged on PATCH) ─────
  ratesPerBody?: BodyRateMap;
  ratesPerBodyMulti?: BodyRateMap;
  driverShare?: MoneyString;
  helperShare?: MoneyString;
  driverShareMulti?: MoneyString;
  helperShareMulti?: MoneyString;
  keterangan?: string;
}

export type UpdateRingPayload = Partial<CreateRingPayload> & { active?: boolean };

// ─────────────────────────────────────────────────────────────────────────────
// Errors + envelope
// ─────────────────────────────────────────────────────────────────────────────

export class TariffApiError extends Error {
  status?: number;
  /** Machine-readable rule code (e.g. `TARIFF_OVERLAP`); absent on plain errors. */
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'TariffApiError';
    this.status = status;
    this.code = code;
  }
}

const toError = (error: any): TariffApiError =>
  new TariffApiError(
    getBackendErrorMessage(error),
    error?.response?.status,
    typeof error?.response?.data?.code === 'string'
      ? error.response.data.code
      : undefined
  );

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

/**
 * Mutation responses wrap the row one level (`data: { tariff }` /
 * `data: { ring }`, see the collection). Peel that key when present; a bare
 * row (older server) passes through unchanged.
 */
const unwrapRow = <T>(payload: unknown, key: 'tariff' | 'ring'): T => {
  const body = unwrap<any>(payload);
  return body && typeof body === 'object' && key in body && body[key]
    ? (body[key] as T)
    : (body as T);
};

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const tariffsAPI = {
  list: async (params: TariffListParams = {}): Promise<TariffConfig[]> => {
    try {
      const response = await Axios.get('/v1/tariffs', {
        params: {
          ...(params.clientId != null ? { clientId: params.clientId } : {}),
          ...(params.activeOn ? { activeOn: params.activeOn } : {}),
        },
      });
      const body = unwrap<{ data: TariffConfig[] } | TariffConfig[]>(
        response?.data
      );
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  /** Approvers only (403 otherwise). */
  create: async (payload: CreateTariffPayload): Promise<TariffConfig> => {
    try {
      const response = await Axios.post('/v1/tariffs', payload);
      return unwrapRow<TariffConfig>(response?.data, 'tariff');
    } catch (error) {
      throw toError(error);
    }
  },

  update: async (
    id: string,
    payload: UpdateTariffPayload
  ): Promise<TariffConfig> => {
    try {
      const response = await Axios.patch(`/v1/tariffs/${id}`, payload);
      return unwrapRow<TariffConfig>(response?.data, 'tariff');
    } catch (error) {
      throw toError(error);
    }
  },

  listRings: async (tariffId: string): Promise<TariffRing[]> => {
    try {
      const response = await Axios.get(`/v1/tariffs/${tariffId}/rings`);
      const body = unwrap<{ data: TariffRing[] } | TariffRing[]>(response?.data);
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      throw toError(error);
    }
  },

  createRing: async (
    tariffId: string,
    payload: CreateRingPayload
  ): Promise<TariffRing> => {
    try {
      const response = await Axios.post(`/v1/tariffs/${tariffId}/rings`, payload);
      return unwrapRow<TariffRing>(response?.data, 'ring');
    } catch (error) {
      throw toError(error);
    }
  },

  updateRing: async (
    ringId: string,
    payload: UpdateRingPayload
  ): Promise<TariffRing> => {
    try {
      const response = await Axios.patch(`/v1/tariffs/rings/${ringId}`, payload);
      return unwrapRow<TariffRing>(response?.data, 'ring');
    } catch (error) {
      throw toError(error);
    }
  },
};
